import { DELIVERY_METHODS, SEGMENT_STATS } from 'src/components/dispatch/orderAttrFilter';
import { FixedTimeValue, FixedValue, FixedValues } from 'src/entities/import';
import {
  ItemHandlingOfCargoStyleOptions,
  ItemKlassOptions,
  ItemPackageStyleOptions,
} from 'src/entities/orderEntity';
import { PlaceEntity } from 'src/entities/placeEntity';
import { ShipperEntity } from 'src/entities/shipperEntity';

export type FixedFieldType =
  | 'text' | 'email' | 'textarea' | 'date' | 'time' | 'number'
  | 'select' | 'multi' | 'trucks' | 'drivers' | 'shipper' | 'place';

type Side = 'loading' | 'unloading';

const TIME_SUFFIXES = ['at_start', 'at_end', 'at_start_two', 'at_end_two', 'at_start_three', 'at_end_three'];

export const TIME_GROUPS: { loading: string[]; unloading: string[] } = {
  loading: TIME_SUFFIXES.map((suffix) => `loading_${suffix}`),
  unloading: TIME_SUFFIXES.map((suffix) => `unloading_${suffix}`),
};

export const timeGroupOf = (key: string): Side | null => {
  if (TIME_GROUPS.loading.includes(key)) return 'loading';
  if (TIME_GROUPS.unloading.includes(key)) return 'unloading';
  return null;
};

// API はある側に固定時刻が 1 つでもあれば、その側のファイルの時刻を 6 項目とも使わない
export const fixedModeKeysOf = (fixed: FixedValues): Set<string> => {
  const keys = new Set(Object.keys(fixed.values));
  (['loading', 'unloading'] as const).forEach((side) => {
    if (TIME_GROUPS[side].some((key) => isFixedValueSet(fixed.values[key]))) {
      TIME_GROUPS[side].forEach((key) => keys.add(key));
    }
  });
  return keys;
};

const typed = (type: FixedFieldType, keys: string[]): [string, FixedFieldType][] => keys.map((key) => [key, type]);

export const FIXED_FIELD_TYPES: Record<string, FixedFieldType> = Object.fromEntries([
  ...typed('shipper', ['shipper_name']),
  ...typed('text', ['shipper_contact_person', 'shipper_phone_number']),
  ...typed('email', ['shipper_email_address']),
  ...typed('place', ['loading_name', 'unloading_name']),
  ...typed('text', [
    'loading_address', 'unloading_address', 'item_code', 'item_name', 'item_lot_number',
    'item_custom_order_number', 'billing_ancillary_content',
    // 積地は 1 ファイル全行で同じことが多い。列が無くても固定値で入れられるようにする（#405）
    'loading_internal_location_code', 'unloading_internal_location_code',
  ]),
  ...typed('textarea', ['memo']),
  ...typed('date', ['loading_on', 'unloading_on']),
  ...typed('time', [...TIME_GROUPS.loading, ...TIME_GROUPS.unloading]),
  ...typed('number', [
    'loading_staying_minutes', 'unloading_staying_minutes', 'item_count', 'item_total_weight_kg',
    'item_total_volume_m3', 'billing_basic_fee_yen', 'billing_surcharge_fee_yen', 'billing_relay_fee_yen',
    'billing_highway_fee_yen', 'billing_loading_fee_yen', 'billing_ancillary_fee_yen',
    'billing_waiting_time_fee_yen', 'billing_unloading_fee_yen', 'billing_expenses_fee_yen',
  ]),
  ...typed('select', [
    'delivery_method', 'section_type', 'item_packing_style', 'item_klass',
    'item_handling_of_cargo_style', 'item_can_be_mixed',
  ]),
  ...typed('multi', [
    'designated_truck_klasses', 'designated_truck_car_models', 'designated_truck_loading_platform_heights',
    'designated_truck_loading_platform_widths', 'designated_truck_loading_platform_lengths',
    'designated_truck_floor_specifications', 'designated_truck_features',
  ]),
  ...typed('trucks', ['allowed_trucks']),
  ...typed('drivers', ['denied_drivers']),
]);

export const FIXED_SELECT_OPTIONS: Record<string, readonly string[]> = {
  delivery_method: DELIVERY_METHODS,
  section_type: SEGMENT_STATS,
  item_packing_style: ItemPackageStyleOptions,
  item_klass: ItemKlassOptions,
  item_handling_of_cargo_style: ItemHandlingOfCargoStyleOptions,
  item_can_be_mixed: ['可', '不可'],
};

export const FIXED_UNITS: Record<string, string> = {
  loading_staying_minutes: '分',
  unloading_staying_minutes: '分',
  item_total_weight_kg: 'kg',
  item_total_volume_m3: 'm³',
  billing_basic_fee_yen: '円',
  billing_surcharge_fee_yen: '円',
  billing_relay_fee_yen: '円',
  billing_highway_fee_yen: '円',
  billing_loading_fee_yen: '円',
  billing_ancillary_fee_yen: '円',
  billing_waiting_time_fee_yen: '円',
  billing_unloading_fee_yen: '円',
  billing_expenses_fee_yen: '円',
};

export const EMPTY_FIXED_VALUES: FixedValues = { values: {}, loading_place_id: null, unloading_place_id: null };

const isTimeValue = (value: FixedValue): value is FixedTimeValue => !Array.isArray(value) && typeof value === 'object';

export const isFixedValueSet = (value: FixedValue | undefined): boolean => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  return value.time !== '';
};

// API も積地名称が無ければ loading_place_id を落とす。名称の無い地点 id は使い道がない。
export const normalizeFixedValues = (fixed: FixedValues): FixedValues => {
  const values = Object.fromEntries(
    Object.entries(fixed.values ?? {}).filter(([, value]) => isFixedValueSet(value)),
  );
  return {
    values,
    loading_place_id: values.loading_name === undefined ? null : (fixed.loading_place_id ?? null),
    unloading_place_id: values.unloading_name === undefined ? null : (fixed.unloading_place_id ?? null),
  };
};

const canonical = (fixed: FixedValues): string => {
  const normalized = normalizeFixedValues(fixed);
  const entries = Object.entries(normalized.values)
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([key, value]) => [key, isTimeValue(value) ? [value.time, value.relative_day] : value]);
  return JSON.stringify([entries, normalized.loading_place_id, normalized.unloading_place_id]);
};

export const isSameFixedValues = (a: FixedValues, b: FixedValues): boolean => canonical(a) === canonical(b);

export const relativeDayLabel = (day: number): string => {
  if (day === -1) return '前日';
  if (day === 0) return '当日';
  if (day === 1) return '翌日';
  return day > 0 ? `${day}日後` : `${-day}日前`;
};

const timePreview = ({ time, relative_day: day }: FixedTimeValue): string => (
  day === 0 ? time : `${relativeDayLabel(day)} ${time}`
);

export const fixedValuePreview = (
  key: string,
  value: FixedValue | undefined,
  labels: { trucks: Map<number, string>; drivers: Map<number, string> },
): string => {
  if (value === undefined || !isFixedValueSet(value)) return '-';
  if (typeof value === 'string') {
    const unit = FIXED_UNITS[key];
    return unit ? `${value} ${unit}` : value;
  }
  if (Array.isArray(value)) {
    const map = key === 'allowed_trucks' ? labels.trucks : key === 'denied_drivers' ? labels.drivers : null;
    return (value as (string | number)[])
      .map((item) => (map && typeof item === 'number' ? (map.get(item) ?? String(item)) : String(item)))
      .join('、');
  }
  return timePreview(value);
};

const without = (values: FixedValues['values'], keys: string[]): FixedValues['values'] => (
  Object.fromEntries(Object.entries(values).filter(([key]) => !keys.includes(key)))
);

const putIfSet = (target: FixedValues['values'], key: string, value: string | undefined | null) => {
  if (value !== undefined && value !== null && value.trim() !== '') target[key] = value;
};

export const applyShipperMaster = (fixed: FixedValues, shipper: ShipperEntity): FixedValues => {
  const values = without(fixed.values, [
    'shipper_name', 'shipper_contact_person', 'shipper_phone_number', 'shipper_email_address',
  ]);
  putIfSet(values, 'shipper_name', shipper.name);
  putIfSet(values, 'shipper_contact_person', shipper.contact_person);
  putIfSet(values, 'shipper_phone_number', shipper.phone_number);
  putIfSet(values, 'shipper_email_address', shipper.email_address);
  return { ...fixed, values };
};

// 秒 → 分の文字列。API は (Number(分) * 60) の切り上げで秒に戻すので、staying_minutes（Float）を
// そのまま文字列にすると浮動小数誤差で 1 秒増えることがある（31 秒 → 32 秒）。
// API の LegacyComplementToFixedValues#minutes_string と同じ規則で、実際に秒へ戻る最短の文字列を探す。
const minutesString = (seconds: number): string | null => {
  if (seconds % 60 === 0) return String(seconds / 60);
  for (let digits = 1; digits <= 10; digits += 1) {
    const scale = 10 ** digits;
    const floored = Math.floor((seconds * scale) / 60);
    for (const numerator of [floored, floored - 1]) {
      if (numerator >= 0) {
        const integer = Math.floor(numerator / scale);
        const fraction = String(numerator % scale).padStart(digits, '0');
        const text = `${integer}.${fraction}`.replace(/0+$/, '').replace(/\.$/, '');
        if (Math.ceil(Number(text) * 60) === seconds) return text;
      }
    }
  }
  return null;
};

export const applyPlaceMaster = (fixed: FixedValues, side: Side, place: PlaceEntity): FixedValues => {
  const values = without(fixed.values, [`${side}_name`, `${side}_address`, `${side}_staying_minutes`, ...TIME_GROUPS[side]]);
  putIfSet(values, `${side}_name`, place.name);
  putIfSet(values, `${side}_address`, place.address);
  if (place.staying_seconds !== undefined && place.staying_seconds !== null) {
    const minutes = minutesString(place.staying_seconds);
    if (minutes !== null) values[`${side}_staying_minutes`] = minutes;
  }
  (place.place_specified_times_attributes ?? []).slice(0, 3).forEach((time, index) => {
    if (time.start_at) {
      values[TIME_GROUPS[side][index * 2]] = { time: time.start_at, relative_day: time.start_relative_day ?? 0 };
    }
    if (time.end_at) {
      values[TIME_GROUPS[side][index * 2 + 1]] = { time: time.end_at, relative_day: time.end_relative_day ?? 0 };
    }
  });
  return { ...fixed, values, [`${side}_place_id`]: place.id };
};
